import { useEffect, useRef, useState } from "react";
import { Svg } from "./Menu";

/** How long "Link copied" stays on the screen. */
export const COPIED_MS = 2000;

/** The full link of a path in the app, or of the page now; the hash stays, so a link can open a section. */
export function linkOf(href: string | null | undefined, here: { href: string; origin: string }): string {
  return new URL(href ?? here.href, here.origin).href;
}

/** Puts the text on the clipboard; false when the browser refuses. */
export function copyText(text: string, clipboard: Pick<Clipboard, "writeText"> | undefined): Promise<boolean> {
  if (!clipboard) return Promise.resolve(false);
  return Promise.resolve()
    .then(() => clipboard.writeText(text))
    .then(
      () => true,
      () => false,
    );
}

export const ShareIcon = ({ size = 20 }: { size?: number }) => (
  <Svg size={size}>
    <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
  </Svg>
);

/** A button that copies the link of a thing to the clipboard (rule U-26); no href: the page now. */
export function ShareButton({ href, what, testId, size = 20 }: { href?: string | null; what: string; testId: string; size?: number }) {
  const [state, setState] = useState<{ ok: true } | { ok: false; link: string } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const button = useRef<HTMLButtonElement>(null);
  const close = () => {
    setState(null);
    button.current?.focus();
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const share = () => {
    const link = linkOf(href, window.location);
    void copyText(link, navigator.clipboard).then((ok) => {
      console.info(`share: ${ok ? "copied" : "the browser refused"} the link of ${new URL(link).pathname}`);
      setState(ok ? { ok: true } : { ok: false, link });
      window.clearTimeout(timer.current);
      if (ok) timer.current = window.setTimeout(() => setState(null), COPIED_MS);
    });
  };
  return (
    <span
      className="share"
      onKeyDown={(e) => {
        if (e.key === "Escape" && state) {
          e.stopPropagation();
          close();
        }
      }}
    >
      <button ref={button} type="button" className="icon-button share-button" aria-label={`Copy the link to ${what}`} title="Copy the link" data-testid={testId} onClick={share}>
        <ShareIcon size={size} />
      </button>
      <span role="status" className={state ? "share-note" : "share-note empty"} data-testid={`${testId}-note`}>
        {state?.ok ? (
          "Link copied"
        ) : state ? (
          <>
            The copy did not work. Copy this link:{" "}
            <input readOnly value={state.link} aria-label="The link" onFocus={(e) => e.currentTarget.select()} />
            <button type="button" className="quiet" onClick={close}>
              Close
            </button>
          </>
        ) : null}
      </span>
    </span>
  );
}
